import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatGBP } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function BookingReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [status, setStatus] = useState("polling");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const max = 8;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (bookingId) {
            try {
              const b = await api.get(`/bookings/${bookingId}`);
              setBooking(b.data);
            } catch {/* ignore */}
          }
          return;
        }
        if (data.status === "expired" || attempts >= max) {
          setStatus("failed");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= max) { setStatus("failed"); return; }
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [sessionId, bookingId]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="mx-auto animate-spin text-[#8DC63F]" size={42} />
          <h1 className="font-display text-3xl font-bold mt-4 text-white">Confirming your payment…</h1>
          <p className="text-gray-400 mt-2">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="mx-auto w-16 h-16 rounded-full bg-[#8DC63F] grid place-items-center"><CheckCircle2 size={36} className="text-black" /></div>
          <h1 className="font-display text-4xl font-black mt-4 text-white" data-testid="payment-success">Booking confirmed!</h1>
          <p className="text-gray-300 mt-2">Thanks — your deposit is paid and your slot is reserved.</p>
          {booking && (
            <div className="wa-card mt-6 text-left">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">Reference</div>
              <div className="font-mono text-sm text-white">{booking.id}</div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">When</div>
                  <div className="font-semibold text-white">{booking.slot_date} {booking.slot_time}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Address</div>
                  <div className="font-semibold text-white">{booking.postcode}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Estimated total</div>
                  <div className="font-semibold text-white">{formatGBP(booking.estimated_total)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Balance after weighing</div>
                  <div className="font-semibold text-white">{formatGBP(booking.balance_due)}</div>
                </div>
              </div>
            </div>
          )}
          <Link to="/" className="wa-btn-primary mt-6 inline-flex">Back to home</Link>
        </>
      )}
      {status === "failed" && (
        <>
          <AlertCircle className="mx-auto text-red-500" size={42} />
          <h1 className="font-display text-3xl font-bold mt-4 text-white">We couldn&apos;t confirm your payment</h1>
          <p className="text-gray-400 mt-2">No worries — please try again or contact us.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Link to="/book" className="wa-btn-primary">Try booking again</Link>
            <Link to="/contact" className="wa-btn-outline">Contact us</Link>
          </div>
        </>
      )}
    </div>
  );
}
